import { CustomerGroupResponse } from "@/models/response/customerGroup";
import customerGroupService from "@/services/customerGroup";
import { Button, TextInput, Textarea } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import BottomSheet from "./BottomSheet";
import { findGroup } from "./groups";
import { GROUP_NAME_MAX, faNumber } from "./sms";
import classes from "./sms.module.scss";

const suggestions = ["مشتریان ویژه", "مشتریان جدید", "عروس‌ها", "مشتریان غیرفعال"];

type Props = {
  opened: boolean;
  onClose: () => void;
  // The new group, or undefined when it was created but could not be looked up.
  onCreated: (group?: CustomerGroupResponse) => void;
};

export const CREATE_SHEET = "createGroup";

export default function CreateGroupSheet({ opened, onClose, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);
  const trimmed = name.trim();

  const create = useMutation({
    mutationFn: async () => {
      await customerGroupService.addCustomerGroup({ name: trimmed, description: description.trim() });
      return findGroup((group) => group.name?.trim() === trimmed, trimmed).catch(() => undefined);
    },
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: ["customerGroup"] });
      setName("");
      setDescription("");
      setTouched(false);
      onCreated(group);
    },
  });

  const submit = () => {
    setTouched(true);
    if (trimmed) create.mutate();
  };

  return (
    <BottomSheet opened={opened} onClose={onClose} name={CREATE_SHEET} title="گروه جدید">
      <form
        className={classes.noticeText}
        style={{ gap: 16 }}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className={classes.noticeText} style={{ gap: 6 }}>
          <TextInput
            label={<span className={classes.label}>نام گروه</span>}
            placeholder="مثلاً مشتریان ویژه"
            size="md"
            radius={12}
            maxLength={GROUP_NAME_MAX}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            error={touched && !trimmed ? "نام گروه را بنویسید" : undefined}
            data-autofocus
          />
          <span className={classes.helperRow} style={{ justifyContent: "flex-end" }}>
            <span>{faNumber(Array.from(name).length)} / {faNumber(GROUP_NAME_MAX)}</span>
          </span>
        </div>
        <div className={classes.varGroup}>
          <span className={classes.subLabel}>پیشنهاد</span>
          <div className={classes.chipRow}>
            {suggestions.map((suggestion) => (
              <button type="button" key={suggestion} className={classes.choiceChip} aria-pressed={trimmed === suggestion} onClick={() => setName(suggestion)}>
                {suggestion}
              </button>
            ))}
          </div>
        </div>
        <Textarea
          label={
            <span className={classes.label}>
              توضیح <span className={classes.mutedLabel}>(اختیاری)</span>
            </span>
          }
          description="فقط برای خودتان، تا یادتان بماند این گروه برای چیست."
          inputWrapperOrder={["label", "input", "description"]}
          placeholder="مثلاً خرید بالای ۵۰ میلیون ریال در سه ماه اخیر"
          size="md"
          radius={12}
          autosize
          minRows={2}
          maxRows={4}
          maxLength={1000}
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
        />
        <Button type="submit" fullWidth h={52} radius={14} color="myColor.7" fz={15} fw={800} loading={create.isLoading} rightSection={<IconChevronLeft size={19} stroke={2.2} />}>
          ساختن و افزودن اعضا
        </Button>
      </form>
    </BottomSheet>
  );
}
